import React from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  useTheme,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
} from "@mui/icons-material";
import type {
  PublicCircleSortField,
  SortOrder,
} from "components/Slices/circleApiSlice";
import { SORT_OPTIONS } from "../constants";

interface FilterBarProps {
  searchInput: string;
  onSearchChange: (value: string) => void;
  onClearSearch: () => void;
  sortBy: PublicCircleSortField | "";
  onSortByChange: (value: PublicCircleSortField | "") => void;
  sortOrder: SortOrder;
  onSortOrderChange: (value: SortOrder) => void;
  onClearAll: () => void;
}

export const FilterBar: React.FC<FilterBarProps> = ({
  searchInput,
  onSearchChange,
  onClearSearch,
  sortBy,
  onSortByChange,
  sortOrder,
  onSortOrderChange,
  onClearAll,
}) => {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 3,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        display: "flex",
        alignItems: "center",
        gap: 2,
        flexWrap: "wrap",
      }}
    >
      <TextField
        size="small"
        placeholder="Search circles..."
        value={searchInput}
        onChange={(e) => onSearchChange(e.target.value)}
        sx={{ minWidth: 220 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
          endAdornment: searchInput && (
            <InputAdornment position="end">
              <Button
                size="small"
                onClick={onClearSearch}
                sx={{ minWidth: "auto", p: 0.5 }}
              >
                <ClearIcon fontSize="small" />
              </Button>
            </InputAdornment>
          ),
        }}
      />

      <Typography variant="body2" color="text.secondary" fontWeight={500}>
        Sort by:
      </Typography>

      <FormControl size="small" sx={{ minWidth: 160 }}>
        <Select
          value={sortBy}
          displayEmpty
          onChange={(e) =>
            onSortByChange(e.target.value as PublicCircleSortField | "")
          }
          renderValue={(selected) => {
            const match = SORT_OPTIONS.find((o) => o.value === selected);
            return match ? (
              match.label
            ) : (
              <Typography
                component="span"
                sx={{ color: "text.secondary", fontSize: 14, fontWeight: 500 }}
              >
                Newest first
              </Typography>
            );
          }}
          inputProps={{ "aria-label": "Sort circles by" }}
          sx={{ fontWeight: 500, fontSize: 14 }}
        >
          {SORT_OPTIONS.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              sx={{ fontWeight: 500, fontSize: 14 }}
            >
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {sortBy && (
        <ToggleButtonGroup
          value={sortOrder}
          exclusive
          onChange={(_, newValue) => newValue && onSortOrderChange(newValue)}
          size="small"
          aria-label="sort order"
        >
          <ToggleButton value="asc" aria-label="ascending">
            <ArrowUpwardIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="desc" aria-label="descending">
            <ArrowDownwardIcon fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
      )}

      {(sortBy || searchInput) && (
        <Button
          size="small"
          variant="text"
          onClick={onClearAll}
          sx={{ ml: "auto", textTransform: "none" }}
        >
          Clear All
        </Button>
      )}
    </Paper>
  );
};
